/*
 * (c) Copyright Reserved EVRYTHNG Limited 2021. All rights reserved.
 * Use of this material is subject to license.
 * Copying and unauthorised use of this material strictly prohibited.
 */

import { totalTimeTraceTransaction } from './sentry';

/**
 * @typedef {Object} Sdk
 * @property {function} getChildScans Check if an Action has child Scan Actions.
 * @property {function} createAction Create a new Action.
 */

/**
 * Create a container for SDK-related functions.
 * @param {string} accessToken Access token to access the EVRYTHNG API.
 * @param {string} [region] Region of EVRYTHNG API server to call: 'us' or 'eu'.
 * @returns {Sdk} Object with functions to perform through the SDK.
 */
export default function createSdk(accessToken, region = null) {
  // Bundler includes SDK and initialises window.evrythng
  const { setup, AccessToken } = window.evrythng;
  setup({
    ...(region && { region }),
    geolocation: false,
  });

  const accessTokenScope = new AccessToken(accessToken);

  return {
    getChildScans: (...args) => getChildScans(accessTokenScope, ...args),
    createAction: (...args) => createAction(accessTokenScope, ...args),
  };
}

/**
 * Check if the parent Action has child Completed Scan Actions.
 * @param {AccessToken} accessTokenScope Instance of AccessToken scope defined
 *                                       in evrythng.js SDK.
 * @param {string} parentActionId  ID of the parent Action
 * @returns {boolean} Whether there are existing child completed scans
 */
async function getChildScans(accessTokenScope, parentActionId) {
  const sentryTransactionGetChildScansSpan =
    totalTimeTraceTransaction.startChild({
      data: { parentActionId },
      op: 'http',
      description: `getting child scans`,
    });

  const childScans = await accessTokenScope.action('scans').read({
    params: {
      // Nesting an object under filter e.g. `filter: { tags: 'from:<parentActionId>' }`
      // causes colon to be wrongly doubly-encoded '%253A' and returns no scans,
      // as it calls '/actions/scans?filter=tags%3Dfrom%253A<parentActionId>'
      filter: `tags=from:${parentActionId}`,
    },
  });

  sentryTransactionGetChildScansSpan.finish();

  return childScans.length > 0;
}

/**
 * Create an Action.
 * @param {AccessToken} accessTokenScope Instance of AccessToken scope defined
 *                                       in evrythng.js SDK.
 * @param {Object} param Action creation parameters.
 * @param {string} param.actionType Default Action type to create Action with.
 *                                  Usually 'scans' or 'referrals'.
 * @param {string} param.consumerId Unique auto-generated ID for visitor.
 * @param {string} [param.parentActionId] ID of parent Action to link the
 *                                        newly-created Action to.
 * @param {string} param.product Product ID associated with the Action.
 * @param {string} [param.thng] Thng ID associated with the Action.
 * @returns {Promise} Promise resolving to the result of the Action creation.
 */
async function createAction(accessTokenScope, params) {
  const sentryTransactionCompletedScanSpan =
    totalTimeTraceTransaction.startChild({
      data: params,
      op: 'http',
      description: `creating action`,
    });

  const { actionType, consumerId, parentActionId, product, thng } = params;

  const action = await accessTokenScope
    .product(product)
    .action(actionType)
    .create({
      type: actionType,
      consumerId,
      ...(parentActionId && {
        customFields: { parentAction: parentActionId },
        tags: [`from:${parentActionId}`],
        identifiers: { scanId: `from:${parentActionId}` },
      }),
      product,
      ...(thng && { thng }),
    });

  sentryTransactionCompletedScanSpan.finish();

  return action;
}
